/**
 * Centralized Product Filter Chokepoint
 *
 * Single source of truth for product allow/deny rules used everywhere we render
 * a product carousel, list, or recommendation. Created May 9, 2026 after the
 * second wave of an audit found 17 carousel surfaces and 5 API routes leaking
 * pdp_eligible=false products (defrost thermostats, hinge bushings, dust
 * covers, fence lights, halloween blender covers) into bestseller/related/etc
 * rails — all because each carousel had its own ad-hoc Supabase query that
 * forgot one or more of the four filters that matter:
 *
 *   1. status = 'active'           (handled per-call — cheap)
 *   2. pdp_eligible = true         (THE bypass that caused the regression)
 *   3. NOT title ILIKE %banned%    (parts/accessories title patterns)
 *   4. NOT brand IN (banned)       (Makspro/Yesparts/etc parts-only brands)
 *
 * Plus a per-type minimum price floor enforced separately when the carousel
 * sorts ascending by price (so cheap parts don't dominate "Best Value" rails).
 *
 * Usage:
 *   import { applySafeProductFilters, PRODUCT_TYPE_PRICE_FLOOR_CENTS } from '@/lib/product-filters'
 *   let q = supabase.from('products').select('...').eq('status', 'active')
 *   q = applySafeProductFilters(q)
 *
 * Design notes:
 * - Re-exported by lib/recommendations.ts for backwards compat (existing
 *   imports of BANNED_TITLE_KEYWORDS / BANNED_PARTS_BRANDS / applyApprovedFilters
 *   continue to work unchanged).
 * - The constants live HERE; lib/recommendations.ts is now a thin wrapper.
 * - Real-or-Hidden Rule 3: filters are SQL-level only. Nothing here touches
 *   ratings / review counts. Those must always be the real value or null —
 *   never a hardcoded 4.5 fallback. The lint script
 *   scripts/lint-no-fake-ratings.sh enforces that.
 */

// Allow only our residential refrigeration types present in DB. Mirrors
// docs/Page-Types-And-Templates.md — keep in sync if either changes.
export const APPROVED_TYPES = [
  // Refrigerators (dash-based to match DB)
  'french-door',
  'side-by-side',
  'top-freezer',
  'bottom-freezer',
  'compact',
  // Freezers
  'upright-freezer',
  'chest-freezer',
  'compact-freezer',
  // Specialty + travel/outdoor
  'wine-cooler',
  'beverage-cooler',
  'kegerator',
  'ice-maker',
  // Legacy underscore variants (backward compatibility)
  'french_door',
  'side_by_side',
  'top_freezer',
  'bottom_freezer',
  'Bottom_Freezer',
  'mini_fridge',
  'upright_freezer',
  'chest_freezer',
  'compact_freezer',
  'wine_cooler',
  'beverage_center',
  'ice_maker',
  'outdoor_fridge',
  'rv_fridge',
  'car_fridge',
  'beauty_fridge',
  'portable_fridge',
] as const

// Title-pattern denylist. Each entry is a substring matched case-insensitively
// against `title`. Be SPECIFIC — bare words like "spring", "cover", "hinge",
// "key" appear in real fridge feature bullets. The patterns below are tight
// enough to only match parts/accessory titles.
//
// Full history: this list grew through three audits in May 2026 after parts
// kept leaking into homepage and PDP carousels. See lib/recommendations.ts
// (pre-extraction) commit history for the round-by-round annotations.
export const BANNED_TITLE_KEYWORDS = [
  // Commercial/industrial
  'commercial', 'restaurant', 'industrial', 'walk-in', 'reach-in', 'nsf', 'etl', 'prep table', 'display case', 'merchandiser',

  // Non-powered cooling (explicit terms only)
  'ice chest', 'insulated bag', 'thermal bag', 'passive cooling',

  // Accessories & parts (existing list)
  'filter', 'water filter', 'ice tray', 'replacement part', 'replacement', 'accessory', 'organizer', 'tray', 'magnet', 'shelf', 'drawer', 'handle',
  'cleaner', 'cleaning', 'deodorizer', 'thermometer', 'water line', 'supply line',

  // Repair-specific terms — tight enough to avoid false positives on real fridges
  'door switch replaces', 'door switch -', 'door switch for refrigerator',
  'door closer cam', 'door closing cam', 'set screw',
  'hinge lever fits', 'hinge cam riser', 'hinge replacement',
  'defrost thermostat', 'defrost timer', 'defrost bi-metal', 'thermostat for refrigerator',
  'dairy door', 'water tube', 'water valve', 'inlet valve',
  'temperature sensor', 'thermistor', 'fan motor',
  'control board', 'main board', 'power board', 'pcb assembly',

  // Covers / non-product items
  'fridge cover', 'refrigerator cover', 'freezer cover', 'dust cover', 'dust-proof cover', 'dustproof cover',
  'appliance case', 'blender cover', 'mixer cover', 'fingerprint-proof', 'fingerprint proof',

  // Off-topic items leaking through
  'solar fence', 'fence light', 'fence lights', 'pumpkin', 'halloween',
  'price difference', 'parts price', 'genuine oem', 'oem - am ',

  // Round 2 — specific parts-only patterns that leaked into the Compact carousel
  'door recess spring', 'recess spring fits',
  'cover fits freezer', 'cover fits refrigerator', 'cover fits ice', 'cover fits ice machine',
  'ice maker connector', 'ice machine connector', 'icemaker connector',
  'door hinge bushing', 'hinge bushing', 'hinge fits refrigerator',
  'compression thread', 'braided stainless steel - 1/4', 'braided stainless steel-1/4',
  'replaces 8', 'replacement part for', 'replaces ap', 'replaces ps',
  'fits refrigerator 8', 'fits freezer 8', 'fits ice machine',
  'key (c293a)', 'c293a key',
  'wr02x', 'w10711', 'w10711162',
  'da63-', 'da61-', 'da97-', 'da32-', 'da81-',
  'ea285013', 'ah285013', 'ps285013',
  'wp2208493b', 'wp2318082', 'wp2315531', 'wp12049801', 'wp2198202',
  'mds64172919', 'mjx61892901', 'adx73270508',
  'wr23x', 'wr32x', 'wr72x', 'wr55x',
  'wh13x',

  // Refrigerator/freezer COMPONENTS (not whole appliances)
  'wire harness', 'door light switch', 'door light', 'light board',
  'shaft fits', 'valve water', 'water valve fits',
  'pan top asm', 'snack pan right side', 'slide rail replacement',
  'gasketdoor', 'gasket assemblydoor', 'door cam closer', 'cam closer',
  'door top hinge', 'door pivot block', 'guide pivot block',
  'thermostat control replacement', 'thermostat replacement',
  'capacitor for refrigerators', 'run capacitor', 'refrigerator part -',
  'refrigerator hinge french', 'refrigerator divider door spring',
  'led refrigerator', 'led light refrigerator', 'led light bulb replace',
  'light bulb replacement model specific', 'refrigerator light socket',
  'refrigerator garage kit', 'fridge cleaning kit', 'refrigerator cleaning brush',
  'refrigerator defrost kit', 'fridge maintenance', 'door seals drain',
  'thermometer replacement', 'refrigerator therm ', 'refrigerator therm,',
  'refrigerator door spring', 'door spring 2pcs',

  // Child-proofing / locks / mats / lures
  'child proof refrigerator lock', 'fridge/freezer door lock',
  'child proof locks for drawer', 'cabinet locks/refrigerator locks',
  'baby proofing', 'no tools need',
  'mini fridge silicone', 'fridge mat', 'under refrigerator mat',
  'paddle tail grub', 'fishing lure', 'soft plastic',

  // Tools & installation
  'dolly', 'installation kit', 'tool', 'strap', 'moving',

  // Components/repair terms
  'pcb', 'module', 'inverter', 'compressor', 'evaporator', 'gasket', 'seal',

  // Other appliances (exclude)
  'dishwasher', 'oven', 'range', 'microwave', 'washer', 'dryer', 'air conditioner', 'dehumidifier',
] as const

// Brands that exclusively sell parts/accessories. Verified May 9, 2026 by
// sampling the live DB. Perlick / Generic intentionally NOT here — they sell
// real refrigerators too. Title-keyword filters catch their parts.
export const BANNED_PARTS_BRANDS = [
  'Makspro',
  'MaksPRO',
  'Yesparts',
  'YesParts',
  'Supplying Demand',
  'Lanruixin',
  'Heoath',
  'Wilniry',
  'Heyiarbeit',
  'Chenjin',
  'Palksky',
  'Zsodio',
  'Hotop',
  'Sikawai',
  'Vosoirsi',
  'Erp',
  'Cctp',
  'Nghtmre',
  'Wreero',
  'Van Sanshao Years',
  'Vansanshao',
  'Delixike',
  'Pnsicei',
  'Strike King',
  'Support',
  'Shark Industrial',
  'Fluidmaster',
] as const

// Per-type minimum price floor (cents). A real appliance of each type has a
// known floor — anything cheaper is almost certainly a part or accessory that
// slipped past type classification. Verified against live catalog May 9, 2026.
export const PRODUCT_TYPE_PRICE_FLOOR_CENTS: Record<string, number> = {
  // Full-size refrigerators — minimum $300
  'refrigerator': 30000,
  'french-door': 30000,
  'french_door': 30000,
  'side-by-side': 30000,
  'side_by_side': 30000,
  'top-freezer': 25000,
  'top_freezer': 25000,
  'bottom-freezer': 25000,
  'bottom_freezer': 25000,
  'Bottom_Freezer': 25000,
  'counter-depth': 30000,
  'built-in': 30000,
  '4-door-flex': 30000,
  'column': 30000,
  'outdoor': 30000,
  'drawer': 30000,
  // Compact / mini — minimum $80
  'compact': 8000,
  'mini_fridge': 8000,
  'compact-freezer': 8000,
  'compact_freezer': 8000,
  'beauty_fridge': 8000,
  'rv_fridge': 8000,
  'car_fridge': 8000,
  'portable_fridge': 8000,
  'outdoor_fridge': 8000,
  // Freezers — minimum $150
  'upright-freezer': 15000,
  'upright_freezer': 15000,
  'chest-freezer': 15000,
  'chest_freezer': 15000,
  'deep-freezer': 15000,
  'freezerless': 15000,
  // Specialty — minimum $80
  'wine-cooler': 8000,
  'wine_cooler': 8000,
  'beverage-cooler': 8000,
  'beverage-center': 8000,
  'beverage_center': 8000,
  'kegerator': 15000,
  'ice-maker': 8000,
  'ice_maker': 8000,
}

// Replacement-filter product types — never show in bestsellers/top-rated
export const FILTER_PRODUCT_TYPES = [
  'water-filter',
  'air-filter',
  'condenser-filter',
  'ice-machine-filter',
  'inline-filter',
] as const

export function isFilterProductType(type: string): boolean {
  return (FILTER_PRODUCT_TYPES as readonly string[]).includes(type)
}

export interface SafeFilterOptions {
  /**
   * Whether to constrain to APPROVED_TYPES via .in('type', ...). Default true.
   * Set false when the caller already filters by a specific type (e.g.
   * collection pages that target a single type — the .eq('type', ...) is
   * tighter than the IN clause).
   */
  constrainTypes?: boolean
}

/**
 * Escape a banned keyword so it means, as a regex branch, exactly what it
 * means as the LIKE pattern `%keyword%`: a literal substring.
 *
 * The keywords carry no LIKE wildcards — `bannedTitleKeywordsAreLiteral()`
 * proves it and tests/product-filters-banned-title-regex.test.ts fails if a
 * future keyword adds one. So each `title NOT ILIKE '%kw%'` is a plain
 * case-insensitive substring test, and an alternation of the escaped
 * keywords is its exact equivalent.
 */
export function escapeForTitleRegex(keyword: string): string {
  return keyword.replace(/[.^$*+?()[\]{}|\\]/g, '\\$&')
}

/** Every keyword is a literal substring — no LIKE wildcard may appear in one. */
export function bannedTitleKeywordsAreLiteral(): boolean {
  return !(BANNED_TITLE_KEYWORDS as readonly string[]).some((k) => /[%_]/.test(k))
}

/**
 * Factor literal alternatives by shared prefixes without changing their language.
 * Each terminal contributes the empty suffix; each edge contributes one escaped
 * character followed by its child's suffixes. Induction over the trie therefore
 * gives exactly the input strings, including a keyword that prefixes another.
 * No boundaries, case folding, wildcard substitution, or substring pruning are
 * added. Insertion order makes the SQL literal deterministic for the source list.
 * An empty list returns the same empty sentinel as the legacy join below.
 */
export function buildFactoredTitlePattern(keywords: readonly string[]): string {
  interface Node {
    terminal: boolean
    children: Map<string, Node>
  }
  const root: Node = { terminal: false, children: new Map() }
  for (const keyword of keywords) {
    let node = root
    for (const character of keyword) {
      let child = node.children.get(character)
      if (!child) {
        child = { terminal: false, children: new Map() }
        node.children.set(character, child)
      }
      node = child
    }
    node.terminal = true
  }
  function render(node: Node): string {
    const branches = node.terminal ? [''] : []
    for (const [character, child] of node.children) {
      branches.push(escapeForTitleRegex(character) + render(child))
    }
    return branches.length > 1 ? `(?:${branches.join('|')})` : (branches[0] ?? '')
  }
  return render(root)
}

/**
 * The legacy banned-title predicate, as ONE case-insensitive regex.
 *
 * This used to be 196 separate `title NOT ILIKE '%kw%'` filters, and that was
 * the single most expensive thing the site asked of Postgres (FRIDGE-869).
 * Measured on the live database against the homepage fridges rail, warm cache,
 * identical 1,176-row result in every variant:
 *
 *   196 x NOT ILIKE ....... 101 ms planning + 487 ms execution
 *   one !~* regex ..........  48 ms planning + 312 ms execution
 *   no title predicate .....   3 ms planning +  14 ms execution
 *
 * So the list WAS the query: 97% of it. The bitmap scan, the heap fetch and
 * the top-N sort together are 14 ms; the rest was 196 substring scans per row
 * over ~1,300 rows, plus the planner re-deriving 196 clauses on every call.
 *
 * Equivalence is not assumed. It was proved against the whole catalog before
 * this replaced the loop — 32,903 rows, 10,034 titles banned by the
 * OR-of-ILIKEs, 10,034 banned by this regex, ZERO disagreements — and
 * tests/product-filters-banned-title-regex.test.ts re-proves it per keyword.
 *
 * It is also the predicate a partial index can carry, which is the rest of the
 * win: supabase/migrations/20260905090000_products_rail_serving_index.sql
 * builds an index whose WHERE clause is this exact expression. The original
 * index was verified present and valid on 2026-09-12. Its predicate removes
 * execution-time title checks, but complete current homepage SQL still spends
 * about 200 ms in planning. The historical measurements above are a different
 * query checkpoint, not current acceptance evidence.
 *
 * Keep this literal bound to the original index for rollout compatibility.
 */
export const LEGACY_BANNED_TITLE_PATTERN: string = (BANNED_TITLE_KEYWORDS as readonly string[])
  .map(escapeForTitleRegex)
  .join('|')

/**
 * STAGED candidate for the additive factored serving index (FRIDGE-869).
 * All 196 keywords remain authoritative and unchanged. The exact generated
 * pattern is bound by test to 20260912225732_products_rail_serving_factored_index.sql.
 * Do not activate it until the FRIDGE-160 owner ruling permits index application
 * and complete live SQL/card/loader acceptance passes. A rewrite without its
 * matching index was measured slower. The old index remains for rollback.
 */
export const FACTORED_BANNED_TITLE_PATTERN = buildFactoredTitlePattern(BANNED_TITLE_KEYWORDS)

// Activation is an explicit reviewed alias change after the staged index and
// live acceptance above. No environment flag or automatic fallback is involved.
export const BANNED_TITLE_PATTERN: string = LEGACY_BANNED_TITLE_PATTERN

/**
 * The ONE chokepoint. Apply this to every product-list query that renders a
 * carousel, recommendation, or browseable listing. It enforces:
 *
 *   - pdp_eligible = true (the publish-gate decision)
 *   - title NOT matching BANNED_TITLE_PATTERN (the BANNED_TITLE_KEYWORDS list)
 *   - brand NOT IN BANNED_PARTS_BRANDS
 *   - type IN APPROVED_TYPES (when constrainTypes=true, the default)
 *
 * The status='active' filter is left to the caller because some routes need
 * to inspect non-active products (e.g. pricing analysis). Most callers should
 * still set it.
 *
 * Returns the modified Supabase query builder (chainable).
 */
export function applySafeProductFilters(query: any, options: SafeFilterOptions = {}): any {
  const { constrainTypes = true } = options
  let q = query.eq('pdp_eligible', true)

  if (constrainTypes) {
    q = q.in('type', APPROVED_TYPES as unknown as string[])
  }

  // One `title !~* '...'` in place of 196 `title NOT ILIKE '%...%'` — see
  // BANNED_TITLE_PATTERN for the measurements and the equivalence proof.
  if (BANNED_TITLE_PATTERN.length > 0) {
    q = q.not('title', 'imatch', BANNED_TITLE_PATTERN)
  }

  if (BANNED_PARTS_BRANDS.length > 0) {
    const csv = (BANNED_PARTS_BRANDS as readonly string[])
      .map((b) => `"${b.replace(/"/g, '\\"')}"`)
      .join(',')
    q = q.not('brand', 'in', `(${csv})`)
  }

  return q
}

/**
 * Real-or-Hidden Rule 3 helpers. Use these instead of `|| 4.5` /
 * `|| Math.floor(Math.random()*500)+100` fallbacks. If the underlying value
 * isn't a real number, return null so the UI hides the rating row entirely.
 */
export function realRating(value: unknown): number | null {
  return typeof value === 'number' && value > 0 ? value : null
}

export function realReviewCount(value: unknown): number | null {
  return typeof value === 'number' && value > 0 ? value : null
}
